import { Locale, translate } from "./translations";

const en: Locale = {
  welcome: "Welcome to APAATO",
  signIn: "Sign In",
  register: "Register",
  accountType: "Account type",
  accountTypeUser: "User",
  accountTypePartner: "Partner",
  firstName: "First name",
  lastName: "Last name",
  orgName: "Organization name",
  createAccount: "Create account",
  registrationRequired: "Please complete registration to continue.",
  signOut: "Sign Out",
  greeting: (name: string) => `Hello, ${name}`,
  email: "Email",
  password: "Password",
  continueWithGoogle: "Continue with Google",
  pleaseWait: "Please wait...",
  fillAllFields: "Please fill out all fields.",
  userNotFound: "No user found with this email.",
  wrongPassword: "Incorrect password.",
  authFailed: "Authentication failed. Please try again.",
  signInCancelled: "Sign-in process was cancelled.",
  googleSignInFailed: "Google sign-in failed. Please try again.",
  notAuthorized: "Your account is not authorized to access this environment.",
  registrationFailed: "Registration failed. Please try again.",

  confirmPassword: "Confirm password",
  accountTypeUserHint: "I want to find an apartment in Japan",
  accountTypePartnerHint: "I want to post housing ads",
  show: "Show",
  hide: "Hide",
  emailRequired: "Email is required",
  emailInvalid: "Please enter a valid email address",
  passwordRequired: "Password is required",
  passwordMin8: "Password must be at least 8 characters",
  passwordNeedNumber: "Password must contain at least 1 number",
  passwordNeedSymbol: "Password must contain at least 1 symbol",
  passwordNeedUpper: "Password must contain at least 1 uppercase letter",
  passwordNeedLower: "Password must contain at least 1 lowercase letter",

  passwordRequirements: "Password requirements",
  passwordReqMin8: "At least 8 characters",
  passwordReqNumber: "At least 1 number",
  passwordReqSymbol: "At least 1 symbol",
  passwordReqUpper: "At least 1 uppercase letter",
  passwordReqLower: "At least 1 lowercase letter",

  notVerified: "Please verify your email before signing in.",
  forbidden: "You are not allowed to access this environment.",

  confirmPasswordRequired: "Confirm your password",
  passwordsDontMatch: "Passwords do not match",
  firstNameRequired: "First name is required",
  lastNameRequired: "Last name is required",
  orgNameRequired: "Organization name is required",

  // Email verification flow
  verificationSentTitle: "Verify your email",
  verificationSentBody: (email: string) =>
    `We sent a verification email to ${email}. Please check your inbox and follow the link to verify your account.`,
  okGotIt: "OK, got it",
  emailConfirmedTitle: "Email confirmed",
  emailConfirmedBody:
    "Thank you! Your email is verified. You can now log into your account.",
  logIntoAccount: "Log into account",

  // Verification page
  verificationFailedTitle: "Verification failed",
  verificationExpired:
    "This verification link has expired. Please request a new one.",
  verificationInvalid:
    "This verification link is invalid or has already been used.",
  verificationFailed: "Unable to verify your email. Please try again later.",
  verificationMissingCodeTitle: "Missing verification code",
  verificationMissingCodeBody:
    "No verification code was provided. Please click the link from your email again.",
  backToLogin: "Back to sign in",
  resendVerificationEmail: "Resend verification email",
  resendVerificationHint: (email: string) =>
    `Would you like us to send a new verification link to ${email}?`,
  verificationResent:
    "A new verification email has been sent. Please check your inbox.",
  resendFailed: "Failed to send verification email. Please try again.",

  // Homepage
  dashboard: "Dashboard",
  homeHeroTitle: "Find your perfect home in Japan",
  homeHeroSubtitle:
    "Discover quality apartments in Tokyo, curated for expats and locals alike.",
  searchPrefecture: "Prefecture",
  searchSelectPrefecture: "Select prefecture",
  searchCity: "City",
  searchSelectCity: "Select city",
  searchLocation: "Location",
  searchSelectArea: "Select area",
  searchMoveIn: "Move-in date",
  searchMoveInPlaceholder: "Select date",
  searchOccupants: "Occupants",
  searchRentBudget: "Monthly budget",
  searchRoomLayout: "Room layout",
  searchPerson: "person",
  searchPeople: "people",
  searchButton: "Search apartments",
  searchEditCriteria: "Edit search",
  featureQualityTitle: "Quality homes",
  featureQualityDesc: "Every listing is verified for quality and comfort.",
  featureTrustedTitle: "Trusted partners",
  featureTrustedDesc: "Work with verified agencies and landlords.",
  featureSupportTitle: "Local support",
  featureSupportDesc: "Multilingual team ready to help you settle in.",
  disclaimerTitle: "MVP in Progress",
  disclaimerBody:
    "This is an early MVP version. Search functionality and listings are coming soon. Currently only Tokyo area will be supported.",
  footerRights: "All rights reserved.",

  // Philosophy Section
  philosophyTitle: "Our Philosophy",
  philosophySubtitle:
    "We want to simplify apartment hunting in Japan for foreigners.",
  philosophyNoFeesTitle: "No Hidden Fees",
  philosophyNoFeesDesc:
    "Complete price transparency. What you see is what you pay — no surprises.",
  philosophySimpleTitle: "Simple Interface",
  philosophySimpleDesc:
    "Clean, intuitive design that makes finding your next home a breeze.",
  philosophyQualityTitle: "Quality Listings",
  philosophyQualityDesc:
    "High-quality listings with all required details about property and apartments.",
  philosophyVerifiedTitle: "Verified Realtors",
  philosophyVerifiedDesc:
    "Work only with trusted, verified real estate professionals.",

  // Snackbar messages
  welcomeUserSnackbar: "Welcome! Dashboard features are coming soon.",
  welcomeAdminSnackbar: "Welcome back, admin!",

  // Dashboard
  dashboardWelcome: "Welcome back",
  dashboardSubtitle: "Manage your apartment search and saved listings.",
  dashboardSearch: "Search",
  dashboardSearchDesc: "Find apartments in Tokyo",
  dashboardFavorites: "Favorites",
  dashboardFavoritesDesc: "View saved listings",
  dashboardNotifications: "Notifications",
  dashboardNotificationsDesc: "Updates on your searches",
  dashboardSettings: "Settings",
  dashboardSettingsDesc: "Manage your account",
  dashboardComingSoon: "Features Coming Soon",
  dashboardComingSoonDesc:
    "These features are currently under development. Check back soon!",
  dashboardDevTools: "Developer Tools",
  dashboardDevToolsDesc: "API access for testing and development",
  dashboardTokenInfo: "Use your ID token to authenticate API requests in",
  dashboardShowToken: "Show Token",
  dashboardCopyToken: "Copy Token",
  dashboardCopied: "Copied!",
  dashboardTokenError: "Unable to get token. Try refreshing the page.",

  // Overview Page
  back: "Back",
  overviewSearchingIn: "Searching in",
  overviewListView: "List",
  overviewMapView: "Map",
  overviewResultsTitle: "Search Results",
  overviewNoResults: "0 apartments found",
  overviewEmptyTitle: "No apartments found",
  overviewEmptyDesc:
    "Adjust your filters or search criteria to find more apartments. Data will be populated soon.",
  overviewMapTitle: "Map View",
  overviewMapComingSoon: "Map View Coming Soon",
  overviewMapComingSoonDesc:
    "Interactive map with apartment locations will be available in a future update.",
  overviewLoading: "Searching apartments...",
  overviewResults: "apartments found",
  overviewErrorTitle: "Something went wrong",
  overviewRetry: "Try again",
  overviewLoadingMore: "Loading more...",
  overviewLoadMore: "Load more",

  // Listing Card
  "listings.min": "min",
  "listings.month": "mo",
  "listings.foreignerFriendly": "Foreigner OK",
  "listings.petsAllowed": "Pets OK",
  "listings.airConditioning": "AC",
  "listings.deposit": "Deposit",
  "listings.keyMoney": "Key",

  // Filters
  filtersTitle: "Filters",
  filterReset: "Reset",

  // Filter Categories
  filterCategoryProperty: "Property Type",
  filterCategoryPrice: "Price & Fees",
  filterCategorySize: "Size & Layout",
  filterCategoryAmenities: "Amenities",
  filterCategoryBuilding: "Building",
  filterCategoryAccess: "Access",
  filterCategoryContract: "Contract",

  // Property Types
  filterPropertyType: "Property type",
  filterPropertyApartment: "Apartment",
  filterPropertyMansion: "Mansion",
  filterPropertyShareHouse: "Share House",
  filterPropertyGuestHouse: "Guest House",
  filterPropertyMonthlyMansion: "Monthly Mansion",

  // Room Layouts
  filterRoomLayout: "Room layout",
  filterLayout1R: "1R",
  filterLayout1K: "1K",
  filterLayout1DK: "1DK",
  filterLayout1LDK: "1LDK",
  filterLayout2K: "2K",
  filterLayout2DK: "2DK",
  filterLayout2LDK: "2LDK",
  filterLayout3K: "3K",
  filterLayout3DK: "3DK",
  filterLayout3LDKPlus: "3LDK+",

  // Price Filters
  filterMinRent: "Min rent",
  filterMaxRent: "Max rent",
  filterIncludeManagementFee: "Include management fee",
  filterKeyMoney: "Key money",
  filterDeposit: "Deposit",
  filterGuarantor: "Guarantor required",
  filterNoKeyMoney: "No key money",
  filterNoDeposit: "No deposit",
  filterHalfMonth: "0.5 month",
  filter1Month: "1 month",
  filter2Months: "2 months",
  filterAny: "Any",
  filterYes: "Yes",
  filterNo: "No",

  // Size Filters
  filterMinSize: "Min size",
  filterMaxSize: "Max size",

  // Amenities
  filterAirConditioning: "Air conditioning",
  filterWasherHookup: "Washer hookup",
  filterWasherDryerInUnit: "Washer/dryer in-unit",
  filterBathToiletSeparate: "Bath/toilet separate",
  filterBalcony: "Balcony",
  filterFlooringWood: "Wood flooring",
  filterInternetIncluded: "Internet included",
  filterGasStove: "Gas stove",
  filterIHCooktop: "IH cooktop",

  // Building
  filterBuildingAge: "Building age",
  filterAnyAge: "Any age",
  filterUnder5Years: "Under 5 years",
  filterUnder10Years: "Under 10 years",
  filterUnder20Years: "Under 20 years",
  filterUnder30Years: "Under 30 years",
  filterFloor: "Floor",
  filterAnyFloor: "Any floor",
  filterFirstFloor: "1st floor",
  filter2ndFloorPlus: "2nd floor+",
  filter3rdFloorPlus: "3rd floor+",
  filterTopFloor: "Top floor",
  filterElevator: "Elevator",
  filterAutoLock: "Auto-lock entrance",
  filterDeliveryBox: "Delivery box",
  filterParking: "Parking available",
  filterBicycleParking: "Bicycle parking",

  // Access
  filterWalkToStation: "Walk to station",
  filterAnyWalkTime: "Any distance",
  filterWithin5Min: "Within 5 min",
  filterWithin10Min: "Within 10 min",
  filterWithin15Min: "Within 15 min",
  filterWithin20Min: "Within 20 min",

  // New categories
  filterCategoryBathroom: "Bathroom & Laundry",
  filterCategoryKitchen: "Kitchen",
  filterCategorySecurity: "Security",
  filterCategoryListing: "Listing",

  // Bathroom & Laundry
  filterIndoorWasherSpace: "Indoor washer space",
  filterReheatingBath: "Reheating bath",
  filterBathroomDryer: "Bathroom dryer",
  filterWashletSeat: "Washlet/bidet seat",
  filterIndependentVanity: "Independent vanity",

  // Kitchen
  filterSystemKitchen: "System kitchen",
  filterCounterKitchen: "Counter kitchen",
  filterDishwasher: "Dishwasher",

  // Building extras
  filterSouthFacing: "South-facing",
  filterCornerUnit: "Corner unit",
  filterBuildingStructure: "Building structure",
  filterStructureRC: "RC (Reinforced Concrete)",
  filterStructureSteel: "Steel",
  filterStructureWood: "Wooden",
  filterStructureSRC: "SRC",

  // Security
  filterSecurityCamera: "Security camera",
  filterVideoIntercom: "Video intercom",

  // Parking extras
  filterMotorcycleParking: "Motorcycle parking",

  // Contract
  filterForeignerFriendly: "Foreigner-friendly",
  filterPetsAllowed: "Pets allowed",
  filterInstrumentsOK: "Musical instruments OK",
  filterSohoOK: "SOHO/Office OK",
  filterNoGuarantorNeeded: "No guarantor needed",
  filterMoveInWithin30Days: "Move-in within 30 days",

  // Listing freshness
  filterListingFreshness: "Listing freshness",
  filterFreshnessAny: "Any time",
  filterFreshness24h: "Last 24 hours",
  filterFreshness3d: "Last 3 days",
  filterFreshness7d: "Last 7 days",
  filterFreshness14d: "Last 14 days",

  // Theme switcher
  themeSwitchToLight: "Switch to light mode",
  themeSwitchToDark: "Switch to dark mode",
};

export const t = (key: keyof Locale, ...args: string[]) =>
  translate(en, key, ...args);
